<script setup lang="ts">
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import InputRequiredCheckbox from '@/vue/atoms/input-required-checkbox.vue';
import HtmlFormatted from '@/vue/molecules/html-formatted.vue';
import {ref} from 'vue';
import {QForm} from 'quasar';
import type {UserConsents, ConsentApprovalRequest} from '@/ts/types/dto/user-dto';
import type {GetLegalDocsResponse} from '@/ts/types/dto/tos-dto';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useUserStore} from '@/ts/store/user-store';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {useLogger} from '@/ts/composables/pure/use-logger';

const props = defineProps<{
  consents: UserConsents;
  legalDocs?: GetLegalDocsResponse;
}>();

const emit = defineEmits<{
  accepted: [];
  declined: [];
}>();

const api = useApi();
const router = useRouter();
const userStore = useUserStore();
const logger = useLogger();
const showModal = defineModel<boolean>({required: true});
const consentForm = ref<QForm>(null!);

const isProcessing = ref(false);
const tosModalOpened = ref(false);
const privacyModalOpened = ref(false);

async function submitConsent(
  approved: boolean,
  consentType: ConsentApprovalRequest['consentType'],
): Promise<void> {
  const request: ConsentApprovalRequest = {
    consentType,
    approved,
  };

  await api.me.putConsentApproval(request);
}

async function handleDecline(): Promise<void> {
  isProcessing.value = true;
  try {
    // Submit decline for terms and conditions if needed
    if (!props.consents.termsAndConditions?.approved || props.consents.termsAndConditions?.needsReapproval) {
      await submitConsent(false, 'terms_and_conditions');
    }

    // Submit decline for privacy policy if needed (shown together with T&C)
    if (!props.consents.privacyPolicy?.approved || props.consents.privacyPolicy?.needsReapproval) {
      await submitConsent(false, 'privacy_policy');
    }

    showModal.value = false;
    emit('declined');
  } catch (error) {
    logger.error('Failed to submit decline consent:', error)();
    // Even if API call fails, we still log out and redirect
    showModal.value = false;
    emit('declined');
  } finally {
    // Same per-user-state cleanup as endLocalSession's non-idToken branch:
    // userStore.logout() resets reactive Pinia state, localStorage.clear()
    // wipes per-user keys (tour-seen, preferences, etc.) so the next user
    // on this browser starts fresh, persistentStorage.clear() re-establishes
    // browser-default lang.
    userStore.logout();
    localStorage.clear();
    persistentStorage.clear();

    // Redirect to public home page
    void router.replace(pagesPath.public.public);

    isProcessing.value = false;
  }
}

async function handleFormSubmit(): Promise<void> {
  isProcessing.value = true;
  try {
    // Submit consent for terms and conditions if needed
    if (!props.consents.termsAndConditions?.approved || props.consents.termsAndConditions?.needsReapproval) {
      await submitConsent(true, 'terms_and_conditions');
    }

    // Submit consent for privacy policy if needed (shown together with T&C)
    if (!props.consents.privacyPolicy?.approved || props.consents.privacyPolicy?.needsReapproval) {
      await submitConsent(true, 'privacy_policy');
    }

    showModal.value = false;
    emit('accepted');
  } catch (error) {
    logger.error('Failed to submit accept consent:', error)();
  } finally {
    isProcessing.value = false;
  }
}
</script>

<template>
  <balm-dialog 
    v-model="showModal" 
    theme="neutral-0" 
    type="fit-content"
    :no-x="true"
    :no-backdrop-dismiss="true"
    persistent
    role="dialog"
    :aria-labelledby="'consent-modal-title'"
    aria-modal="true"
  >
    <div class="terms-change-modal">
      <div class="modal-header">
        <h2 id="consent-modal-title" class="modal-title">
          {{ $t('legal_documents_update_title') }}
        </h2>
      </div>

      <div class="modal-content">
        <q-form 
          ref="consentForm" 
          :aria-labelledby="'consent-modal-title'"
          @submit="handleFormSubmit"
        >
          <p class="consent-subtitle">
            {{ $t('legal_documents_update_description') }}
          </p>
          
          <!-- Terms and Conditions Agreement (TOS + Privacy Policy) -->
          <div
            v-if="(
              !props.consents.termsAndConditions?.approved ||
              props.consents.termsAndConditions?.needsReapproval ||
              !props.consents.privacyPolicy?.approved ||
              props.consents.privacyPolicy?.needsReapproval
            ) &&
              props.legalDocs?.termsAndConditions &&
              props.legalDocs?.privacyPolicy"
            class="consent-checkbox-container"
          >
            <input-required-checkbox class="agreement-check">
              <span>{{ $t('i_accept_the') }}</span>&nbsp;<span
                role="button"
                tabindex="0"
                class="link-text"
                :aria-label="$t('view_terms_of_use')"
                @click.stop="tosModalOpened = true"
                @keydown.enter.prevent="tosModalOpened = true"
                @keydown.space.prevent="tosModalOpened = true"
              >{{ $t('terms_of_use') }}</span>
              <span>&nbsp;{{ $t('and') }}&nbsp;</span><span
                role="button"
                tabindex="0"
                class="link-text"
                :aria-label="$t('view_privacy_policy')"
                @click.stop="privacyModalOpened = true"
                @keydown.enter.prevent="privacyModalOpened = true"
                @keydown.space.prevent="privacyModalOpened = true"
              >{{ $t('privacy_policy') }}</span>
            </input-required-checkbox>
          </div>
          
          <div class="buttons">
            <balm-btn
              :label="$t('accept')"
              theme="blue"
              paddings="p2-5"
              type="submit"
              :loading="isProcessing"
              :disable="isProcessing"
              :aria-describedby="'consent-modal-title'"
            />
            <balm-btn
              :label="$t('decline')"
              theme="grey"
              paddings="p2-5"
              :loading="isProcessing"
              :disable="isProcessing"
              :aria-describedby="'consent-modal-title'"
              @click="handleDecline"
            />
          </div>
        </q-form>
      </div>
    </div>
  </balm-dialog>
  
  <!-- Terms of Use Modal -->
  <balm-dialog v-if="props.legalDocs" v-model="tosModalOpened" theme="neutral-0">
    <html-formatted :html="props.legalDocs.termsAndConditions.legalCopyHTML" background="neutral-0"/>
  </balm-dialog>
  
  <!-- Privacy Policy Modal -->
  <balm-dialog v-if="props.legalDocs" v-model="privacyModalOpened" theme="neutral-0">
    <html-formatted :html="props.legalDocs.privacyPolicy.legalCopyHTML" background="neutral-0"/>
  </balm-dialog>
</template>

<style scoped lang="scss">
.terms-change-modal {
  padding: var(--Spacing-spacing-4);
  max-height: 90vh;
  overflow-y: auto;
  
  @include screen-lg {
    min-width: 500px;
    max-width: 700px;
  }
}

.modal-header {
  text-align: center;
}

.modal-title {
  margin-bottom: var(--Spacing-spacing-4);
}

.consent-subtitle {
  margin-bottom: var(--Spacing-spacing-4);
}

.consent-checkbox-container {
  margin-bottom: var(--Spacing-spacing-4);
}

.agreement-title {
  @include font-body-bold;
  margin-bottom: var(--Spacing-spacing-3);
}

.agreement-check  {
  @include font-body;
  :deep(.q-field__control) {
    color: var(--Neutral-UI-neutral-ui-100)
  }
  :deep(.q-checkbox__label) {
    padding-left: var(--Spacing-spacing-4);
  }
}

.link-text {
  @include focus-visible;
  text-decoration: underline;
  cursor: pointer;
}

.buttons {
  display: flex;
  gap: var(--Spacing-spacing-3);
  justify-content: center;
  margin-top: var(--Spacing-spacing-6);
  margin-bottom: var(--Spacing-spacing-6);

  @media (max-width: 480px) {
    flex-direction: column;

    :deep(.q-btn) {
      width: 100%;
    }
  }
}
</style>